/**
 * MapPreviewCard — Universal preview card for map pins.
 *
 * For ACTIVITIES: matches Base Camp ActivityCard exactly
 *   Badge → Title → Time + Location (inline) → "X going" → Action
 *
 * For other types: compact info layout
 */

import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card } from '../ui/card';
import {
  X, ChevronRight, MapPin, Users, Clock,
  Check, Star, Landmark, Building, Briefcase
} from 'lucide-react';

const ACTIVITY_STATUS_STYLES = {
  idea: { bg: '#FEF3C7', text: '#92400E', label: 'Idea' },
  confirmed: { bg: '#D1FAE5', text: '#065F46', label: 'Confirmed' },
  active: { bg: '#DBEAFE', text: '#1E40AF', label: 'Active' },
  completed: { bg: '#F3F4F6', text: '#6B7280', label: 'Completed' },
  good_to_go: { bg: '#D1FAE5', text: '#065F46', label: 'Good to go' },
  needs_people: { bg: '#FEF3C7', text: '#92400E', label: 'Needs people' },
  at_risk: { bg: '#FEE2E2', text: '#991B1B', label: 'At risk' },
  canceled: { bg: '#F3F4F6', text: '#6B7280', label: 'Canceled' },
};

function deriveMapLiveState(preview) {
  const status = preview.status || 'idea';
  if (status === 'completed') return 'completed';
  if (status === 'canceled') return 'canceled';
  const pCount = preview.participant_count || 0;
  const startTime = preview.start_time ? new Date(preview.start_time) : null;
  const now = new Date();
  const hoursUntil = startTime ? (startTime - now) / (1000 * 60 * 60) : Infinity;
  if (hoursUntil < 4 && hoursUntil > 0 && pCount < 2) return 'at_risk';
  if (pCount >= 3) return 'good_to_go';
  return 'needs_people';
}

const TYPE_STYLES = {
  activity: { label: 'Activity', color: '#7A8F5B', icon: Check },
  group: { label: 'Group', color: '#E8A87C', icon: Users },
  commons: { label: 'Commons', color: '#7FA5B8', icon: Landmark },
  institution: { label: 'Institution', color: '#7B2D3B', icon: Building },
  business: { label: 'Business', color: '#3B6FA0', icon: Briefcase },
  sponsor: { label: 'Sponsor', color: '#C9A227', icon: Star },
};

export default function MapPreviewCard({ pin, onAction, onDismiss, isGuest }) {
  const navigate = useNavigate();
  const [showGuestOverlay, setShowGuestOverlay] = useState(false);
  if (!pin) return null;

  const typeInfo = TYPE_STYLES[pin.type] || TYPE_STYLES.activity;
  const preview = pin.preview || {};
  const isActivity = pin.type === 'activity';

  const handleAction = () => {
    if (isGuest) {
      setShowGuestOverlay(true);
      return;
    }
    if (onAction) onAction();
  };

  // Activity card — matches Base Camp ActivityCard exactly
  if (isActivity) {
    const statusStyle = ACTIVITY_STATUS_STYLES[deriveMapLiveState(preview)] || ACTIVITY_STATUS_STYLES.needs_people;
    const startTime = preview.start_time ? new Date(preview.start_time) : null;
    const pCount = preview.participant_count || 0;

    return (
      <div className="absolute bottom-6 left-0 right-0 z-[1000] flex justify-center pointer-events-none px-4" style={{ animation: 'lk-card-rise 0.25s ease-out' }}>
        <Card
          className="w-full max-w-md pointer-events-auto overflow-hidden p-3 cursor-pointer transition-all active:scale-[0.98] relative"
          style={{ backgroundColor: 'var(--fy-surface-card)', border: '1px solid var(--fy-border-subtle)', boxShadow: '0 4px 16px rgba(0,0,0,0.1)' }}
          onClick={handleAction}
          data-testid="map-preview-card"
        >
          {/* Guest inline overlay */}
          {showGuestOverlay && (
            <div className="absolute inset-0 z-10 flex flex-col items-center justify-center rounded-xl" style={{ backgroundColor: 'rgba(244,239,230,0.95)', backdropFilter: 'blur(4px)' }}>
              <p className="text-sm font-medium mb-3" style={{ color: 'var(--fy-text-primary)' }}>Create a free account to join</p>
              <button
                onClick={(e) => { e.stopPropagation(); navigate('/auth?mode=signin'); }}
                className="px-5 py-2 rounded-full text-sm font-bold"
                style={{ backgroundColor: 'var(--lk-deep-moss, #4A5A3A)', color: '#fff' }}
                data-testid="guest-overlay-continue"
              >
                Continue
              </button>
              <button
                onClick={(e) => { e.stopPropagation(); setShowGuestOverlay(false); }}
                className="absolute top-2 right-2 p-1 rounded-full"
                data-testid="guest-overlay-dismiss"
              >
                <X className="w-4 h-4" style={{ color: 'var(--fy-text-disabled)' }} />
              </button>
            </div>
          )}

          <div className="flex items-start gap-3">
            <div className="flex-1 min-w-0">
              {/* Badge row — matches BaseCamp exactly */}
              <div className="flex items-center gap-2 mb-1">
                <span
                  className="px-2 py-0.5 rounded-full text-[10px] font-semibold"
                  style={{ backgroundColor: statusStyle.bg, color: statusStyle.text }}
                >
                  {statusStyle.label}
                </span>
              </div>

              {/* Title — font-medium text-sm truncate */}
              <p className="font-medium text-sm truncate" style={{ color: 'var(--fy-text-primary)' }}>
                {preview.title || 'Untitled'}
              </p>

              {/* Time + Location inline — flex gap-3 mt-1 */}
              <div className="flex items-center gap-3 mt-1">
                {startTime && (
                  <span className="text-xs flex items-center gap-1" style={{ color: 'var(--fy-text-secondary)' }}>
                    <Clock className="w-3 h-3" />
                    {startTime.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}
                    {' '}
                    {startTime.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' })}
                  </span>
                )}
                {preview.location_label && (
                  <span className="text-xs flex items-center gap-1 truncate" style={{ color: 'var(--fy-text-secondary)' }}>
                    <MapPin className="w-3 h-3 flex-shrink-0" />
                    {preview.location_label}
                  </span>
                )}
              </div>

              {/* Participation — "X going" */}
              {pCount > 0 && (
                <span className="text-xs mt-1 inline-block" style={{ color: 'var(--fy-text-disabled)' }}>
                  {pCount} going
                </span>
              )}

              {/* Guest CTA */}
              {isGuest && (
                <div className="mt-2">
                  <span className="text-xs font-medium px-3 py-1 rounded-full inline-block" style={{ backgroundColor: 'var(--fy-surface-muted)', color: 'var(--fy-text-secondary)' }}>
                    Create free account to join
                  </span>
                </div>
              )}
            </div>

            {/* Dismiss + chevron */}
            <div className="flex flex-col items-center gap-1 flex-shrink-0">
              <button
                onClick={(e) => { e.stopPropagation(); onDismiss(); }}
                className="p-1 rounded-full"
                data-testid="preview-dismiss-btn"
              >
                <X className="w-4 h-4" style={{ color: 'var(--fy-text-disabled)' }} />
              </button>
              <ChevronRight className="w-4 h-4 mt-1" style={{ color: 'var(--fy-text-disabled)' }} />
            </div>
          </div>
        </Card>
      </div>
    );
  }

  // Non-activity cards (groups, institutions, businesses, sponsors)
  const TypeIcon = typeInfo.icon;
  return (
    <div className="absolute bottom-6 left-0 right-0 z-[1000] flex justify-center pointer-events-none px-4" style={{ animation: 'lk-card-rise 0.25s ease-out' }}>
      <Card
        className="w-full max-w-md pointer-events-auto shadow-xl overflow-hidden"
        style={{ backgroundColor: 'var(--fy-surface-card)', border: '1px solid var(--fy-border-subtle)' }}
        data-testid="map-preview-card"
      >
        <div className="p-3">
          <div className="flex items-start justify-between gap-2 mb-1">
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2 mb-1">
                <span className="px-2 py-0.5 rounded-full text-[10px] font-semibold inline-flex items-center gap-1" style={{ backgroundColor: `${typeInfo.color}20`, color: typeInfo.color }}>
                  <TypeIcon className="w-3 h-3" />
                  {typeInfo.label}
                </span>
              </div>
              <p className="font-medium text-sm truncate" style={{ color: 'var(--fy-text-primary)' }}>
                {preview.title || preview.name || 'Untitled'}
              </p>
            </div>
            <button onClick={onDismiss} className="p-1 rounded-full flex-shrink-0" data-testid="preview-dismiss-btn">
              <X className="w-4 h-4" style={{ color: 'var(--fy-text-disabled)' }} />
            </button>
          </div>

          {pin.type === 'sponsor' && (
            <p className="text-xs mb-2" style={{ color: 'var(--fy-text-disabled)' }}>
              {preview.title || preview.name || 'This sponsor'} supports local activity.
            </p>
          )}

          {preview.description && pin.type !== 'sponsor' && (
            <p className="text-xs mb-2 line-clamp-2" style={{ color: 'var(--fy-text-secondary)' }}>{preview.description}</p>
          )}
          {preview.member_count > 0 && (
            <span className="text-xs" style={{ color: 'var(--fy-text-disabled)' }}>{preview.member_count} members</span>
          )}

          <button
            onClick={pin.type === 'sponsor' ? () => navigate(`/sponsors/${pin.id}`) : (isGuest ? () => navigate('/auth?mode=signin') : onAction)}
            className="w-full mt-2 py-2 rounded-lg text-sm font-medium flex items-center justify-center gap-1"
            style={{ backgroundColor: `${typeInfo.color}15`, color: typeInfo.color, cursor: 'pointer' }}
            data-testid="preview-open-btn"
          >
            {pin.type === 'sponsor' ? 'View Sponsor' : (isGuest ? 'Sign in to view details' : 'Open')}
            <ChevronRight className="w-4 h-4" />
          </button>
        </div>
      </Card>
    </div>
  );
}
